// Parser & surface point sampler for 3D OBJ models and procedural 3D geometries.
// Samples uniform area-weighted points across triangle surfaces into a Float32Array (count * 4)
// suitable for the STARCODE GPGPU morph target texture.

import * as THREE from "three";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";

/**
 * Samples `count` random points across the surface of a BufferGeometry,
 * centered and scaled to fit within `targetExtent`.
 */
export function buildGeometryTargets(geometry, count, targetExtent = 480) {
  const geom = geometry.index ? geometry.toNonIndexed() : geometry.clone();
  geom.computeBoundingBox();
  geom.computeVertexNormals();

  const bbox = geom.boundingBox;
  const center = new THREE.Vector3();
  bbox.getCenter(center);
  geom.translate(-center.x, -center.y, -center.z);

  const size = new THREE.Vector3();
  geom.computeBoundingBox();
  geom.boundingBox.getSize(size);
  const maxDim = Math.max(size.x, size.y, size.z);
  const scale = maxDim > 0 ? targetExtent / maxDim : 1;

  const posAttr = geom.attributes.position;
  const triCount = Math.floor(posAttr.count / 3);

  const targets = new Float32Array(count * 4);

  if (triCount === 0) {
    // If no triangles exist (e.g. pure point cloud), copy positions directly with wrap
    const vCount = posAttr.count;
    for (let i = 0; i < count; i++) {
      const srcIdx = (i % vCount) * 3;
      targets[i * 4 + 0] = (posAttr.array[srcIdx + 0] - center.x) * scale;
      targets[i * 4 + 1] = (posAttr.array[srcIdx + 1] - center.y) * scale;
      targets[i * 4 + 2] = (posAttr.array[srcIdx + 2] - center.z) * scale;
      targets[i * 4 + 3] = 1.0;
    }
    return targets;
  }

  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  const ab = new THREE.Vector3();
  const ac = new THREE.Vector3();
  const cross = new THREE.Vector3();

  const triAreas = new Float32Array(triCount);
  let totalArea = 0;
  for (let i = 0; i < triCount; i++) {
    a.fromBufferAttribute(posAttr, i * 3 + 0);
    b.fromBufferAttribute(posAttr, i * 3 + 1);
    c.fromBufferAttribute(posAttr, i * 3 + 2);
    ab.subVectors(b, a);
    ac.subVectors(c, a);
    cross.crossVectors(ab, ac);
    const area = cross.length() * 0.5;
    triAreas[i] = area;
    totalArea += area;
  }

  // Cumulative distribution for area-weighted triangle picking
  const cumulative = new Float32Array(triCount);
  let running = 0;
  for (let i = 0; i < triCount; i++) {
    running += triAreas[i];
    cumulative[i] = running;
  }

  function pickTriangleIndex() {
    if (totalArea <= 0) return Math.floor(Math.random() * triCount);
    const r = Math.random() * totalArea;
    let lo = 0;
    let hi = triCount - 1;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (cumulative[mid] < r) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }

  for (let i = 0; i < count; i++) {
    const triIndex = pickTriangleIndex();
    a.fromBufferAttribute(posAttr, triIndex * 3 + 0);
    b.fromBufferAttribute(posAttr, triIndex * 3 + 1);
    c.fromBufferAttribute(posAttr, triIndex * 3 + 2);

    // Uniform random point on triangle surface
    let u = Math.random();
    let v = Math.random();
    if (u + v > 1) {
      u = 1 - u;
      v = 1 - v;
    }
    const px = a.x + u * (b.x - a.x) + v * (c.x - a.x);
    const py = a.y + u * (b.y - a.y) + v * (c.y - a.y);
    const pz = a.z + u * (b.z - a.z) + v * (c.z - a.z);

    targets[i * 4 + 0] = px * scale;
    targets[i * 4 + 1] = py * scale;
    targets[i * 4 + 2] = pz * scale;
    targets[i * 4 + 3] = 1.0;
  }

  return targets;
}

/**
 * Parses raw OBJ text into a target point cloud for particle morphing.
 */
export function parseObjTargets(objText, count, targetExtent = 480) {
  const loader = new OBJLoader();
  const obj = loader.parse(objText);

  const geometries = [];
  obj.traverse((child) => {
    if (child.isMesh && child.geometry) {
      const geom = child.geometry.index ? child.geometry.toNonIndexed() : child.geometry.clone();
      // Apply child matrix if transformed
      child.updateMatrix();
      geom.applyMatrix4(child.matrix);
      geometries.push(geom);
    }
  });

  if (geometries.length === 0) {
    // Fallback: parse raw vertices if loader didn't build meshes (e.g. pure 'v x y z' lines)
    const lines = objText.split("\n");
    const vertices = [];
    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed.startsWith("v ")) {
        const parts = trimmed.split(/\s+/).slice(1).map(Number);
        if (parts.length >= 3 && !parts.some(isNaN)) {
          vertices.push(new THREE.Vector3(parts[0], parts[1], parts[2]));
        }
      }
    }
    if (vertices.length > 0) {
      const geom = new THREE.BufferGeometry().setFromPoints(vertices);
      return buildGeometryTargets(geom, count, targetExtent);
    }
    throw new Error("Keine 3D-Geometrie im OBJ gefunden.");
  }

  // If single geometry, sample directly
  if (geometries.length === 1) {
    return buildGeometryTargets(geometries[0], count, targetExtent);
  }

  // Merge multiple geometries into one buffer
  let totalVerts = 0;
  for (const g of geometries) totalVerts += g.attributes.position.count;
  const mergedPos = new Float32Array(totalVerts * 3);
  let offset = 0;
  for (const g of geometries) {
    const pos = g.attributes.position.array;
    mergedPos.set(pos, offset);
    offset += pos.length;
  }
  const mergedGeom = new THREE.BufferGeometry();
  mergedGeom.setAttribute("position", new THREE.BufferAttribute(mergedPos, 3));
  return buildGeometryTargets(mergedGeom, count, targetExtent);
}

/**
 * Generates an interesting default 3D geometry (Torus Knot) before the user uploads an OBJ.
 */
export function buildDefault3DMeshTargets(count, targetExtent = 480) {
  const geom = new THREE.TorusKnotGeometry(140, 42, 128, 32);
  return buildGeometryTargets(geom, count, targetExtent);
}
